import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import type { TreeTranslationsData } from '@tolgee/react';
import { Tolgee, TolgeeProvider } from '@tolgee/react';
import { FormatIcu } from '@tolgee/format-icu';
import './main.css';
import { routeTree } from '~/routeTree.gen';
import { LanguageConfig } from '@agentselly/immodossier-shared';
import { TolgeeLanguageDetector } from '~/shared/TolgeeLanguageDetector';
import { HtmlLangSync } from '~/shared/HtmlLangSync';
import { LoadingSpinner } from '~/components/global/LoadingSpinner';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { deDE, enGB, frFR, itIT } from '@clerk/localizations';
import { fetchBackend } from '~/helper/fetchBackend.ts';
import { getSignOutRedirectUrl } from '~/helper/getSignOutRedirectUrl';
// App-Back via TanStack location state.from — kein globaler sessionStorage-Stack
// mehr. Siehe AppBackService.

// Load React-Grab DevTools in development
import '~/utils/react-grab-loader';
import {
  computeAttributionTags,
  persistAttribution,
} from '@agentselly/analytics';

const CLERK_KEYS = {
  development: 'pk_test_Y2hhcm1lZC1maXNoLTQuY2xlcmsuYWNjb3VudHMuZGV2JA',
  staging: 'pk_test_Y2hhcm1lZC1maXNoLTQuY2xlcmsuYWNjb3VudHMuZGV2JA',
  production: 'pk_live_Y2xlcmsuYWdlbnRzZWxseS5jaCQ',
};

// Prefer Doppler/Vite env var; fallback by hostname (.dev -> test; else -> prod)
const VITE_ENV_KEY = (import.meta as { env?: Record<string, string> }).env
  ?.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
const host = window.location.hostname;
const isDevHost =
  host === 'localhost' ||
  host.endsWith('.dev') ||
  host.endsWith('.agentselly.dev');
const CLERK_PUBLISHABLE_KEY =
  VITE_ENV_KEY || (isDevHost ? CLERK_KEYS.development : CLERK_KEYS.production);

// HubSpot tracking loader: sets the first-party `hubspotutk` cookie so the
// booking / expose lead submission can be stitched to the tracked visitor by
// HubSpot. portalId comes from the Vite env; on dev hosts we fall back to the
// sandbox portal. On a non-dev host WITHOUT VITE_HUBSPOT_PORTAL_ID the script is
// intentionally NOT injected (so prod visitors are never tracked into the
// sandbox portal) — set VITE_HUBSPOT_PORTAL_ID to enable it in production.
(function injectHubSpotTracking(): void {
  const envPortalId = (import.meta as { env?: Record<string, string> }).env
    ?.VITE_HUBSPOT_PORTAL_ID as string | undefined;
  const portalId = envPortalId || (isDevHost ? '146326428' : '');
  if (!portalId) return;
  if (document.getElementById('hs-script-loader')) return;
  const script = document.createElement('script');
  script.id = 'hs-script-loader';
  script.async = true;
  script.defer = true;
  script.src = `//js.hs-scripts.com/${portalId}.js`;
  document.head.appendChild(script);
})();

// Pirsch Analytics loader: client code comes from the Vite env, matching
// the VITE_HUBSPOT_PORTAL_ID pattern above — the script is intentionally
// NOT injected without VITE_PIRSCH_CODE set, so local/preview environments
// never send data into the production Pirsch site. Fathom (previous
// provider) was cancelled and its script/env var removed.
//
// Pirsch's `pa.js` tracks programmatic URL changes (History API) as page
// views automatically by default — see
// https://docs.pirsch.io/get-started/frontend-integration. Because this
// already covers every client-side route change AND the initial load, no
// manual pageview call is added in __root.tsx — that would double-count
// every route change as two pageviews (this mirrors the same reasoning the
// removed Fathom `data-spa="auto"` setup relied on).
(function injectPirschTracking(): void {
  // Runtime-injected env from the backend's SpaFallbackController takes
  // precedence: VITE_* vars never reach the production bundle (the GHA
  // build has no Doppler access), so import.meta.env is only a fallback for
  // environments where the client bundle is built with the env present.
  const envPirschCode =
    (window as any).__ENV__?.VITE_PIRSCH_CODE ||
    ((import.meta as { env?: Record<string, string> }).env?.VITE_PIRSCH_CODE as
      | string
      | undefined);
  if (!envPirschCode) return;
  if (document.getElementById('pianjs')) return;

  // Persist first-touch channel/UTM attribution for this session BEFORE
  // building the script tag: readAttribution()/attributionMeta() (used by
  // trackEvent for every programmatic funnel event) needs it in
  // sessionStorage from here on, and the tag attributes below need it to
  // seed the very first pageview correctly (pa.js reads data-tag-* live off
  // this element, but for a browser-injected <script> that means "as it
  // exists the moment this element is appended to <head>" — set BEFORE
  // appendChild, never after).
  const companyId = window.location.pathname.split('/').filter(Boolean)[1];
  const attribution = persistAttribution({
    url: window.location.href,
    referrer: document.referrer,
    companyId,
  });
  const attributionTags = computeAttributionTags({
    url: window.location.href,
    referrer: document.referrer,
    companyId,
    stored: attribution,
  });

  const script = document.createElement('script');
  script.id = 'pianjs';
  script.src = 'https://api.pirsch.io/pa.js';
  script.setAttribute('data-code', envPirschCode);
  for (const [
    key,
    value,
  ] of Object.entries(attributionTags)) {
    script.setAttribute(`data-tag-${key}`, value);
  }
  script.defer = true;
  // The script is appended dynamically (after DOMContentLoaded may already
  // have fired), so pa.js's own `DOMContentLoaded → pirschInit` listener can
  // miss. pirschInit wires the declarative `data-pirsch-event` scanning and
  // outbound-link tracking — without calling it those would silently not
  // fire. Invoke it once the script has loaded; the optional chain guards
  // adblocked/failed loads.
  script.onload = () => {
    (window as any).pirschInit?.();
  };
  document.head.appendChild(script);
})();

const localizations = {
  de: deDE,
  fr: frFR,
  it: itIT,
  en: enGB,
};

const router = createRouter({
  routeTree: routeTree,
  scrollRestoration: true,
  context: {
    clerkAuth: { isLoaded: false, isSignedIn: false },
    getCurrentRouteId: () => {
      const matches = router?.state?.pendingMatches || [];

      if (!matches.length) {
        matches.push(...(router?.state?.matches || []));
      }

      for (let i = matches.length - 1; i >= 0; i--) {
        const match = matches[i];

        if (match.routeId) {
          return match.routeId;
        }
      }

      return '';
    },
  },
});

// App-Back via TanStack location state.from — kein globaler sessionStorage-Stack
// mehr. Siehe AppBackService. Die Initialisierung entfällt, weil der
// from-State automatisch von AppLink und withFromState gesetzt wird.

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Extend the router context type so beforeLoad guards can access clerkAuth
declare module '@tanstack/react-router' {
  interface RouterContext {
    clerkAuth?: { isLoaded: boolean; isSignedIn: boolean };
  }
}

async function fetchTranslations(lang: string): Promise<TreeTranslationsData> {
  try {
    const response = await fetchBackend(`/language/${lang}`);

    if (!response.ok) {
      throw new Error(
        `fetchTranslations LOCAL ERROR ${response.status} ${response.statusText}`,
      );
    }

    return await response.json();
  } catch (e) {
    console.error('fetchTranslations ERROR', e);

    return {};
  }
}

const tolgee = Tolgee()
  .use(TolgeeLanguageDetector)
  .use(FormatIcu())
  .init({
    availableLanguages: LanguageConfig.availableLanguages,
    fallbackLanguage: LanguageConfig.fallbackLanguage,
    defaultLanguage: LanguageConfig.fallbackLanguage,
    staticData: Object.fromEntries(
      LanguageConfig.availableLanguages.map((language: string) => [
        language,
        async () => fetchTranslations(language),
      ]),
    ),
  });

const queryClient = new QueryClient();

const getLocalization = () => {
  const lang = router.state.location.pathname.split('/')[1];
  return (
    localizations[lang as keyof typeof localizations] ??
    localizations[LanguageConfig.fallbackLanguage]
  );
};

// Clerk v5 integration with TanStack Router:
// Without routerPush/routerReplace, Clerk navigates via window.history.pushState
// directly, which pushes phantom entries into the browser history stack.
// Those phantom entries make router.history.back() land on a Clerk URL instead
// of the previous app page — requiring two taps to go back.
//
// By routing Clerk's navigation through TanStack Router, all navigation stays
// within TanStack's history management and no phantom entries are created.
const resolveClerkTarget = (to: string): string => {
  if (to === '/' || to === '') {
    return getSignOutRedirectUrl(
      undefined,
      router.state?.location?.pathname || window.location.pathname,
    );
  }
  return to;
};

const clerkRouterPush = (to: string) => {
  void router.navigate({ to: resolveClerkTarget(to), replace: false });
};
const clerkRouterReplace = (to: string) => {
  void router.navigate({ to: resolveClerkTarget(to), replace: true });
};

/**
 * RouterWithAuth — renders the TanStack RouterProvider with the live Clerk
 * auth state injected into the router context. The context is merged with
 * the initial context from createRouter, so getCurrentRouteId is preserved.
 *
 * This replaces the previous mutable snapshot + manual router.invalidate()
 * approach with TanStack's canonical context update mechanism: every time
 * Clerk's auth state changes, the router receives a fresh clerkAuth object
 * and beforeLoad guards re-run automatically with the new values.
 */
function RouterWithAuth() {
  const { isLoaded, isSignedIn } = useAuth();

  return (
    <RouterProvider
      router={router}
      context={{ clerkAuth: { isLoaded, isSignedIn: !!isSignedIn } }}
      defaultPendingComponent={() => <LoadingSpinner mainLoader={false} />}
    />
  );
}

createRoot(document.getElementById('root')!).render(
  <Suspense fallback={<LoadingSpinner mainLoader={true} />}>
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <TolgeeProvider
          tolgee={tolgee}
          fallback={<LoadingSpinner mainLoader={true} />}
        >
          <HtmlLangSync />
          <ClerkProvider
            publishableKey={CLERK_PUBLISHABLE_KEY}
            localization={getLocalization()}
            afterSignOutUrl={getSignOutRedirectUrl()}
            routerPush={clerkRouterPush}
            routerReplace={clerkRouterReplace}
          >
            <RouterWithAuth />
          </ClerkProvider>
        </TolgeeProvider>
      </QueryClientProvider>
    </StrictMode>
  </Suspense>,
);
